/**
 * Divider — thin separator line between sibling elements.
 *
 * Goals:
 *   - Defaults to the right orientation based on its flex parent
 *     (row-flex → vertical line, column/block → horizontal line) using
 *     CSS only — no JS layout detection.
 *   - Explicit `orientation` prop wins over auto.
 *   - Optional content slot (`children`) — label / icon centered in the
 *     middle of the line. The line splits and flex-grows on both sides.
 *
 * Color & weight:
 *   - `color` selects a semantic palette role for the line's stroke.
 *     Direct role keys match Figma's Divider variants — `surface1` /
 *     `surface2` / `surface3` / `onSurface` / `primary` / `bad` /
 *     `good` / `alert` / `background`. Default `'surface2'`.
 *   - `weight` is the stroke thickness in pixels. `1` (hairline) or
 *     `2` (heavier). Default `1`.
 *
 * Inset shortens the line on its main axis. Accepts a number (same
 * inset on both ends) or an object `{ start, end }`.
 *
 * @format
 */

import {forwardRef, type CSSProperties, type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {surfaceVar} from 'ui/theme/surfaceContext';

export const DIVIDER_ORIENTATIONS = ['auto', 'horizontal', 'vertical'] as const;
export type DividerOrientation = (typeof DIVIDER_ORIENTATIONS)[number];

export const DIVIDER_COLORS = [
  'background',
  'surface1',
  'surface2',
  'surface3',
  'onSurface',
  'primary',
  'bad',
  'good',
  'alert',
] as const;
export type DividerColor = (typeof DIVIDER_COLORS)[number];

export const DIVIDER_WEIGHTS = [1, 2] as const;
export type DividerWeight = (typeof DIVIDER_WEIGHTS)[number];

function strokeFor(theme: import('@mui/material/styles').Theme, color: DividerColor): string {
  const p = theme.palette;
  switch (color) {
    case 'background':
      return p.background.default;
    case 'surface1':
      return p.surface.surface1;
    case 'surface2':
      return p.surface.surface2;
    case 'surface3':
      return p.surface.surface3;
    case 'onSurface':
      return p.surface.onSurface;
    case 'primary':
      return p.primary.main;
    case 'bad':
      return p.bad.main;
    case 'good':
      return p.good.main;
    case 'alert':
      return p.alert.main;
    default:
      return p.surface.surface2;
  }
}

const Line = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$orientation: DividerOrientation; $color: DividerColor; $weight: DividerWeight}>(({
  theme,
  $orientation,
  $color,
  $weight,
}) => {
  const bg = strokeFor(theme, $color);
  const px = `${$weight}px`;

  const auto = {
    alignSelf: 'stretch',
    flexShrink: 0,
    minWidth: px,
    minHeight: px,
  } as const;

  const horizontal = {
    width: '100%',
    height: px,
    flexShrink: 0,
  } as const;

  const vertical = {
    width: px,
    height: '100%',
    alignSelf: 'stretch',
    flexShrink: 0,
  } as const;

  const orient =
    $orientation === 'horizontal' ? horizontal : $orientation === 'vertical' ? vertical : auto;

  return {
    ...orient,
    backgroundColor: bg,
    border: 0,
  };
});

const ContentWrapper = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$orientation: DividerOrientation; $weight: DividerWeight}>(
  ({theme, $orientation, $weight}) => ({
    display: 'flex',
    flexDirection: $orientation === 'vertical' ? 'column' : 'row',
    alignItems: 'center',
    gap: theme.spacing(3),
    ...($orientation === 'vertical'
      ? {alignSelf: 'stretch', minWidth: `${$weight}px`}
      : {width: '100%', minHeight: `${$weight}px`}),
  }),
);

const ContentSlot = styled(Box)(({theme}) => ({
  flexShrink: 0,
  color: surfaceVar('onSurfaceMuted'),
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

export interface DividerProps {
  orientation?: DividerOrientation;
  color?: DividerColor;
  weight?: DividerWeight;
  inset?: number | {start?: number; end?: number};
  children?: ReactNode;
  role?: string;
  className?: string;
  style?: CSSProperties;
}

export const Divider = forwardRef<HTMLDivElement, DividerProps>(function Divider(
  {
    orientation = 'auto',
    color = 'surface2',
    weight = 1,
    inset,
    children,
    role = 'separator',
    className,
    style,
  },
  ref,
) {
  const insetStart = typeof inset === 'number' ? inset : (inset?.start ?? 0);
  const insetEnd = typeof inset === 'number' ? inset : (inset?.end ?? 0);

  const insetStyle: CSSProperties =
    orientation === 'vertical'
      ? {paddingTop: insetStart, paddingBottom: insetEnd}
      : orientation === 'horizontal'
        ? {paddingLeft: insetStart, paddingRight: insetEnd}
        : {
            paddingLeft: insetStart,
            paddingRight: insetEnd,
            paddingTop: insetStart,
            paddingBottom: insetEnd,
          };

  const merged: CSSProperties = {...insetStyle, ...style};

  if (!children) {
    return (
      <Line
        ref={ref}
        role={role}
        aria-orientation={orientation === 'auto' ? undefined : orientation}
        $orientation={orientation}
        $color={color}
        $weight={weight}
        className={className}
        style={merged}
      />
    );
  }

  return (
    <ContentWrapper
      ref={ref}
      role={role}
      aria-orientation={orientation === 'auto' ? undefined : orientation}
      $orientation={orientation}
      $weight={weight}
      className={className}
      style={merged}
    >
      <Line $orientation={orientation} $color={color} $weight={weight} />
      <ContentSlot>{children}</ContentSlot>
      <Line $orientation={orientation} $color={color} $weight={weight} />
    </ContentWrapper>
  );
});
